import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Badge } from "@chakra-ui/react";
import { Eye, Archive, Plus, EllipsisVertical, ShieldAlert, Pencil } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import { useDispatch, useSelector } from "react-redux";
import {
  fetchPlansAction,
  createPlanAction,
  updatePlanAction,
  deletePlanAction,
} from "../../../state/actions/planActions";

import AddPackagePlanDrawer from "./components/AddPackagePlanDrawer";
import ViewPackagePlanDrawer from "./components/ViewPackagePlanDrawer";
import EditPackagePlanDrawer from "./components/EditPackagePlanDrawer";
import DeletePackagePlanModal from "./components/DeletePackagePlanModal";

import { useCustomNotification } from "components/Toasts/OpenNotification";

const KilometerManagement = () => {
  const dispatch = useDispatch();
  const notify = useCustomNotification();
  const { plans, loading } = useSelector((state) => state.plans);

  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setEditDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    dispatch(fetchPlansAction());
  }, [dispatch]);

  const openAddDrawer = () => setAddDrawerOpen(true);
  const closeAddDrawer = () => setAddDrawerOpen(false);

  const openViewDrawer = (plan) => {
    setSelectedPlan(plan);
    setViewDrawerOpen(true);
  };

  const openEditDrawer = (plan) => {
    setSelectedPlan(plan);
    setEditDrawerOpen(true);
  };

  const openDeleteModal = (plan) => {
    setSelectedPlan(plan);
    setDeleteModalOpen(true);
  };

  const handleAddPlan = async (planData) => {
    try {
      await dispatch(createPlanAction(planData));
      closeAddDrawer();
      notify({ type: "success", message: "Plan Added", description: "Package plan added successfully." });
    } catch (err) {
      notify({ type: "error", message: "Error", description: "Failed to add package plan." });
    }
  };

  const handleUpdatePlan = async (updatedPlan) => {
    try {
      await dispatch(updatePlanAction(updatedPlan.kilometer_package_id, updatedPlan));
      setEditDrawerOpen(false);
      notify({ type: "success", message: "Plan Updated", description: "Package plan updated successfully." });
    } catch (err) {
      notify({ type: "error", message: "Error", description: "Failed to update package plan." });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deletePlanAction(selectedPlan.kilometer_package_id));
      setDeleteModalOpen(false);
      notify({ type: "success", message: "Plan Deleted", description: "Package plan deleted successfully." });
    } catch (err) {
      notify({ type: "error", message: "Error", description: "Failed to delete package plan." });
    }
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View Plan",
      onClick: () => openViewDrawer(row.original),
    },
    {
      icon: <Pencil size={16} />,
      text: "Edit Plan",
      onClick: () => openEditDrawer(row.original),
    },
    {
      icon: <Archive size={16} />,
      text: "Delete Plan",
      onClick: () => openDeleteModal(row.original),
    },
  ];

  const columnsData = [
    { Header: "Name", accessor: "name" },
    { Header: "Deliveries", accessor: "deliveries" },
    { Header: "Price", accessor: "price" },
    { Header: "Expiry Days", accessor: "expiry_days" },
    {
      Header: "Active",
      accessor: "is_active",
      Cell: ({ value }) => (
        <Badge
          colorScheme={value ? "green" : "red"}
          variant="subtle"
          px={2}
          py={1}
          borderRadius="full"
        >
          {value ? "True" : "False"}
        </Badge>
      ),
    },

    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="flex-end" mb={4} pt={{ base: "120px", md: "25px" }}>
        {loading ? (
          <Skeleton width="140px" height="40px" borderRadius="md" />
        ) : (
          <GradientButton icon={<Plus size={16} />} height="40px" mr={5} onClick={openAddDrawer}>
            Add Package Plan
          </GradientButton>
        )}
      </Flex>

      <ReusableTable columnsData={columnsData} tableData={plans} isLoading={loading} />

      <AddPackagePlanDrawer isOpen={isAddDrawerOpen} onClose={closeAddDrawer} onAddPlan={handleAddPlan} />

      <ViewPackagePlanDrawer isOpen={isViewDrawerOpen} onClose={() => setViewDrawerOpen(false)} plan={selectedPlan} />

      <EditPackagePlanDrawer isOpen={isEditDrawerOpen} onClose={() => setEditDrawerOpen(false)} plan={selectedPlan} onUpdatePlan={handleUpdatePlan} />

      <DeletePackagePlanModal isOpen={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleConfirmDelete} plan={selectedPlan} />
    </Box>
  );
};

export default KilometerManagement;
